import { gql } from '@apollo/client/core'
import { AudienceCriterionState } from '../../../gql-gen/questionnaire/graphql'
import { MemberSettingCode } from '../../../model/audience'
import { FieldworkAudienceType } from '../../../model/audience'

interface MarketAudienceSetting {
  code: MemberSettingCode
  value: string
}

interface MarketAudienceResponseOption {
  responseOptionId: string
  value: string
}

interface MarketAudienceCriterionOption {
  criterionOption: { responseOption: MarketAudienceResponseOption }
}

export type MarketAudienceMember = { position: number } & (
  | {
      memberType: FieldworkAudienceType.Custom
      memberItem: {
        __typename?: string
        questionLk: string
        state?: AudienceCriterionState
        question?: { questionId: string; text: string } | null
        criterionOptions: {
          responseOptionLk: string
          responseOption: MarketAudienceResponseOption
        }[]
        settingValues: MarketAudienceSetting[]
      }
    }
  | {
      memberType: FieldworkAudienceType.Standard
      memberItem: {
        __typename?: string
        criterion: {
          name: string
          question: { questionId: string; text: string }
        }
        settingValues: MarketAudienceSetting[]
        standardCriterionOptions: MarketAudienceCriterionOption[]
        customizedCriterionOptions?: MarketAudienceCriterionOption[] | null
      }
    }
)

export interface MarketAudiencesData {
  questionnaireAggV2: {
    marketsSpecification?:
      | {
          key: string
          value: {
            draftAudience: { members: MarketAudienceMember[] }
          }
        }[]
      | null
  } | null
}

// Bounded so a failed load can be reported instead of retried forever.
export const MARKET_AUDIENCES_CONTEXT = {
  clientName: 'questionnaire',
  maxRetryAttempts: 3
}

/**
 * Every market's audience, keyed by survey id. Run it with `fetchPolicy:
 * 'no-cache'`: audience entities normalise by ids shared across markets.
 */
export const GET_MARKET_AUDIENCES = gql`
  query marketAudiencesV2($surveyId: ID!) {
    questionnaireAggV2(surveyId: $surveyId) {
      marketsSpecification {
        key
        value {
          draftAudience {
            members {
              position
              memberType
              memberItem {
                __typename
                ... on DraftCustomAudienceItem {
                  questionLk
                  state
                  question {
                    questionId
                    text
                  }
                  criterionOptions {
                    responseOptionLk
                    responseOption {
                      responseOptionId
                      value
                    }
                  }
                  settingValues {
                    code
                    value
                  }
                }
                ... on DraftStandardAudienceItem {
                  criterion {
                    name
                    question {
                      questionId
                      text
                    }
                  }
                  settingValues {
                    code
                    value
                  }
                  standardCriterionOptions {
                    criterionOption {
                      responseOption {
                        responseOptionId
                        value
                      }
                    }
                  }
                  customizedCriterionOptions {
                    criterionOption {
                      responseOption {
                        responseOptionId
                        value
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`
